import { IconFileImport } from "@/assets/icons/IconFileImport";
import { CardContainer } from "@/components/commons/CardContainer";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { FeaturePagination } from "@/features/learning-content/components/FeaturePagination";
import { TMockExamQuestionData } from "@/features/learning-content/types/learningContent";
import { useTable } from "@/hooks/useTable";
import { ExplanationLanguageCountCell } from "@/features/learning-content/components/ExplanationLanguageCountCell";
import { ImagePreviewCell } from "@/features/learning-content/components/ImagePreviewCell";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Link, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";

type TMockExamDataTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  data: TMockExamQuestionData[];
  totalPage: number;
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  onOpenSheet?: () => void;
  onSync?: () => void;
  isSyncing?: boolean;
};

// Table component for displaying mock exam question data with pagination
// Design: STUDY_ADM_LCM-MockExamShow-01 (Frame 1000005244)
export const MockExamDataTable: FC<TMockExamDataTableProps> = ({
  isLoading,
  isFetching,
  data,
  totalPage,
  totalCount,
  page,
  onPageChange,
  onOpenSheet,
  onSync,
  isSyncing,
}) => {
  const columns = useMemo<MRT_ColumnDef<TMockExamQuestionData>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "questionId",
        size: 60,
        muiTableHeadCellProps: { sx: { width: "60px", minWidth: "60px" } },
        muiTableBodyCellProps: { sx: { width: "60px", minWidth: "60px" } },
      },
      {
        header: "科目名",
        accessorKey: "subjectName",
        size: 120,
        muiTableHeadCellProps: { sx: { width: "120px" } },
        muiTableBodyCellProps: { sx: { width: "120px", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } },
        Cell: ({ row }) => row.original.subjectName || EMPTY_STR.TEXT,
      },
      {
        header: "設問文",
        accessorKey: "questionText",
        size: 150,
        muiTableHeadCellProps: { sx: { width: "150px" } },
        muiTableBodyCellProps: { sx: { width: "150px", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } },
        Cell: ({ row }) => row.original.questionText || EMPTY_STR.TEXT,
      },
      {
        header: "画像",
        accessorKey: "hasImage",
        size: 80,
        muiTableHeadCellProps: { align: "center", sx: { width: "80px", ".Mui-TableHeadCell-Content": { justifyContent: "center" } } },
        muiTableBodyCellProps: { align: "center", sx: { width: "80px" } },
        Cell: ({ row }) => <ImagePreviewCell filePath={row.original.hasImage ? row.original.imageUrl : null} alt="問題画像" />,
      },
      {
        header: "正解の選択肢",
        accessorKey: "correctAnswer",
        size: 150,
        muiTableHeadCellProps: { sx: { width: "150px" } },
        muiTableBodyCellProps: { sx: { width: "150px", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } },
        Cell: ({ row }) => row.original.correctAnswer || EMPTY_STR.TEXT,
      },
      {
        header: "解説言語数",
        accessorKey: "supportedLanguages",
        size: 100,
        muiTableHeadCellProps: { sx: { width: "100px" } },
        muiTableBodyCellProps: { sx: { width: "100px" } },
        Cell: ({ row }) => <ExplanationLanguageCountCell supportedLanguages={row.original.supportedLanguages ?? []} />,
      },
      {
        header: "配点",
        accessorKey: "score",
        size: 60,
        muiTableHeadCellProps: { sx: { width: "60px" } },
        muiTableBodyCellProps: { sx: { width: "60px" } },
        Cell: ({ row }) => row.original.score ?? EMPTY_STR.TEXT,
      },
      {
        header: "追加日",
        accessorKey: "addedDate",
        size: 106,
        muiTableHeadCellProps: { sx: { width: "106px" } },
        muiTableBodyCellProps: { sx: { width: "106px" } },
        Cell: ({ row }) => row.original.addedDate || EMPTY_STR.TEXT,
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows: data ?? [],
    enableSorting: false,
    enableRowActions: false,
  });

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        {/* Header with title and action buttons */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="head20Bold">模擬試験データ&nbsp;({totalCount})</Typography>
          <Stack direction="row" gap={2} alignItems="center">
            {onOpenSheet && (
              <Link
                component="button"
                onClick={onOpenSheet}
                underline="always"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, color: "primary.main" }}
              >
                シートを開く
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            )}
            {onSync && (
              <Button size="sm" variant="outline" onClick={onSync} startIcon={<IconFileImport />} disabled={isSyncing} isLoading={isSyncing}>
                シートから反映
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Info text - matches design exactly */}
        <Typography variant="body14" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
          既定の仕様に基づくExcelファイルをインポートして、問題の追加または更新をしてください。
          <br />
          削除する問題のIDは廃盤として、行ごと削除するかIDを空欄にしてください。IDのない単語は無効化されます。
        </Typography>

        {/* Table */}
        <Table table={table} isLoading={isLoading} isFetching={isFetching} />

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <FeaturePagination page={page} totalPage={totalPage} onChange={onPageChange} />
        </Box>
      </Stack>
    </CardContainer>
  );
};
